/**
 * What people see when an interpreted call is tied to the person who asked
 * for the interpreter (the anchor), in one place for the web portal (script
 * tag, window.MalkaInterpretedAnchor), the web room and the apps (import).
 * Rules and messages: docs/interpreted-group-calls.md.
 */
(function(root, factory) {
    const anchor = factory();
    if (typeof module === 'object' && module.exports) module.exports = anchor;
    else root.MalkaInterpretedAnchor = anchor;
})(typeof globalThis !== 'undefined' ? globalThis : this, function() {
    const ANCHOR_END_REASONS = [ 'anchor_left', 'anchor_disconnected', 'ended_for_all' ];

    function nameOf(value, fallback) {
        const text = typeof value === 'string' ? value.trim() : '';
        return text ? text.slice(0, 80) : fallback;
    }

    /** Whether a call_ended came from the anchor rule. */
    function isAnchorEnd(data) {
        return Boolean(data) && data.surface === 'interpreted_anchor_departure' && ANCHOR_END_REASONS.includes(data.reason);
    }

    /**
     * What happened, when an anchored call ended: who left or dropped. The
     * anchor who hung up is told nothing (they did it). Nothing for any
     * other end. No explanations (AGENTS.md, no explanatory copy).
     */
    function anchorEndNotice(data, localUserId) {
        if (!isAnchorEnd(data)) return undefined;
        const self = Boolean(localUserId) && data.endedBy === localUserId;
        const name = nameOf(data.endedByName, 'The caller');
        if (data.reason === 'anchor_disconnected') return self ? 'You disconnected.' : `${name} disconnected.`;
        if (self) return undefined;
        return data.reason === 'ended_for_all' ? `${name} ended the call.` : `${name} left.`;
    }

    /** Whole seconds left in a grace (a deadline in ms or ISO text), never below 0. */
    function secondsLeft(graceEndsAt, now) {
        const end = typeof graceEndsAt === 'number' ? graceEndsAt : Date.parse(graceEndsAt);
        if (!Number.isFinite(end)) return 0;
        return Math.max(0, Math.ceil((end - (now === undefined ? Date.now() : now)) / 1000));
    }

    /**
     * The banner everyone else sees while someone's reconnect grace runs
     * (react/features/vrs-layout peersReconnecting: { name, graceEndsAt }):
     * "Dana reconnecting…", or "Dana disconnected" once a known deadline has
     * passed. No seconds: the banner is shown once, not redrawn.
     */
    function anchorAwayBanner(peer, now) {
        const name = nameOf(peer && (peer.name || peer.anchorName), 'Someone');
        const deadline = peer && peer.graceEndsAt;
        const over = deadline !== undefined && deadline !== null && secondsLeft(deadline, now) <= 0;
        return over ? `${name} disconnected` : `${name} reconnecting…`;
    }

    /** The interpreter left a call that started person-to-person (p2p_interpreter_left). */
    function interpreterLeftNotice(data) {
        return `${nameOf(data && data.interpreterName, 'The interpreter')} left.`;
    }

    /** The holding screen of a member waiting for the anchor (anchor_hold). */
    function holdCopy(hold) {
        const anchorName = nameOf(hold && hold.anchorName, 'the caller');
        const state = hold && hold.anchorState;
        return {
            title: hold && hold.released ? 'Joining…' : `Waiting for ${anchorName}`,
            anchorState: state === 'joining' || state === 'answered' ? 'Joining' : 'Ringing',
            leaveLabel: 'Leave'
        };
    }

    /**
     * What closed a call-back before the anchor was back (anchor_hold_ended).
     * '' when this person left it themselves: nothing to say.
     */
    function holdEndedNotice(reason, anchorName) {
        const name = nameOf(anchorName, 'The caller');
        switch (reason) {
        case 'anchor_declined': return `${name} declined.`;
        case 'anchor_no_answer': return `${name} didn't answer.`;
        case 'anchor_did_not_join': return `${name} didn't rejoin.`;
        case 'callback_cancelled': return 'Call-back cancelled.';
        case 'interpreter_left': return 'The interpreter left.';
        case 'left': return '';
        default: return 'Call-back ended.';
        }
    }

    /**
     * A refused call-back (error { operation: 'interpreter_callback', code }):
     * the words the interpreter sees, and whether the offer is gone for good.
     */
    function callbackRefusal(code) {
        switch (code) {
        case 'CALLBACK_EXPIRED': return { message: 'Call-back expired.', final: true };
        case 'CALLBACK_LIMIT': return { message: 'Call-back limit reached.', final: true };
        case 'NOT_CALLBACKABLE': case 'FORBIDDEN': case 'NOT_FOUND':
            return { message: 'Call-back unavailable.', final: true };
        case 'CALLBACK_IN_PROGRESS': return { message: 'Already calling back.', final: false };
        default: return { message: 'Call back failed. Try again.', final: false };
        }
    }

    /** The interpreter's room label: 'Interpreting for Dana'. */
    function interpretingForLabel(anchorName) {
        const name = nameOf(anchorName, '');
        return name ? `Interpreting for ${name}` : 'Interpreting';
    }

    /**
     * The interpreter's wrap-up card after the anchor's connection dropped
     * (call_ended.callback). Null when no call-back is offered.
     */
    function callbackOffer(data, now) {
        const callback = data && data.callback;
        if (!callback || callback.available !== true) return null;
        const expires = Date.parse(callback.expiresAt);
        if (!Number.isFinite(expires) || expires <= (now === undefined ? Date.now() : now)) return null;
        const name = nameOf(callback.anchorName, 'them');
        const others = Math.max(0, Number(callback.rosterCount) || 0);
        return {
            title: `${name === 'them' ? 'They' : name} disconnected.`,
            action: others ? `Call back ${name} (and ${others} ${others === 1 ? 'other' : 'others'})` : `Call back ${name}`,
            expiresAt: callback.expiresAt,
            callId: data.callId
        };
    }

    /** One line per person in interpreter_callback_status. */
    function callbackMemberLine(member) {
        const label = {
            ringing: 'Ringing', held: 'Waiting', joined: 'In call', declined: 'Declined',
            missed: 'No answer', left: 'Left'
        }[member && member.state] || 'Ringing';
        return `${nameOf(member && member.name, 'Someone')}${member && member.role === 'anchor' ? ' (the caller)' : ''}: ${label}`;
    }

    /**
     * The interpreter's view of their call-back (interpreter_callback_status):
     * a title, one line per person, and whether Cancel call back is still
     * offered (only until the person they were with is back).
     */
    function callbackStatusView(status) {
        const members = Array.isArray(status && status.members) ? status.members : [];
        const anchor = members.find(member => member && member.role === 'anchor');
        const name = nameOf(anchor && anchor.name, 'them');
        const state = status && status.state;
        let title;
        switch (state) {
        case 'anchor_answered': title = `${name === 'them' ? 'They' : name} joining…`; break;
        case 'anchor_joined': title = `${name === 'them' ? 'They are' : `${name} is`} back`; break;
        // The interpreter cancelled it themselves: nothing to say.
        case 'closed': title = status.closeReason === 'callback_cancelled'
            ? ''
            : holdEndedNotice(status.closeReason, anchor && anchor.name); break;
        default: title = `Calling ${name} back…`;
        }
        return {
            title,
            lines: state === 'closed' ? [] : members.map(callbackMemberLine),
            cancellable: state === 'ringing' || state === 'anchor_answered',
            cancelLabel: 'Cancel call back'
        };
    }

    return {
        ANCHOR_END_REASONS,
        anchorAwayBanner,
        anchorEndNotice,
        callbackMemberLine,
        callbackOffer,
        callbackRefusal,
        callbackStatusView,
        holdCopy,
        holdEndedNotice,
        interpreterLeftNotice,
        interpretingForLabel,
        isAnchorEnd,
        secondsLeft
    };
});
